import React, { useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import SolidButton from '@/components/General/SolidButton';
import SearchBox from '@/components/General/inputs/SearchBox';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import {
    getServiceFlowTemplate,
    saveServiceFlowTemplate,
    setServiceFlowTemplateArchived,
} from '@/data/serviceFlows';
import { showAlert } from '@/utils/showAlert';
import {
    buildTemplateItemInput,
    invalidateServiceFlowTemplateQueries,
    newFlowItemID,
} from '@/utils/serviceFlow';
import { isServiceFlowV2Available, SERVICE_FLOW_LIMITS } from '@/config/serviceFlows';
import ServiceFlowTemplateCard from '@/components/Events/ServiceFlow/ServiceFlowTemplateCard';
import { serviceFlowStyles } from '@/components/Events/ServiceFlow/serviceFlowStyles';

/**
 * Service Flow template library (spec §3.2). Organization-scoped, account-
 * scoped queries, paginated 50/page, searchable, archived toggle.
 */
const ServiceFlowLibraryScreen = () => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const accountID = loggedInUser?.accountID;
    const { data: organization } = Query.organization.byId({ id: organizationID });
    const v2Available = isServiceFlowV2Available(organization);
    const canManage = !!loggedInUser?.can?.('Edit Events');

    const [search, setSearch] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [page, setPage] = useState(0);

    const { data, isLoading, isError, refetch, isFetching } = Query.serviceFlows.templates({
        organizationID,
        accountID,
        archived: showArchived,
        search,
        page,
        options: { enabled: v2Available },
    });
    const templates = data?.templates || [];
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / SERVICE_FLOW_LIMITS.libraryPageSize));

    const archiveMutation = useMutation({
        mutationFn: setServiceFlowTemplateArchived,
        onSuccess: (_r, vars) => {
            invalidateServiceFlowTemplateQueries(queryClient, { organizationID, accountID, templateID: vars.templateID });
            Toast.show({ type: 'success', text2: vars.archived ? 'Template archived.' : 'Template restored.' });
        },
    });

    const duplicateMutation = useMutation({
        mutationFn: async (template) => {
            const full = await getServiceFlowTemplate(template.id);
            if (!full) throw new Error('Template not found');
            return saveServiceFlowTemplate({
                templateID: newFlowItemID(),
                expectedVersion: 0,
                name: `${full.name} (copy)`,
                description: full.description ?? null,
                // Fresh item IDs — the copy shares structure and task references
                // but never assignment, attachment, or source IDs (spec §3.2).
                items: (full.items || []).map((it, i) => ({
                    ...buildTemplateItemInput({ ...it, id: undefined }, i),
                    id: newFlowItemID(),
                })),
            });
        },
        onSuccess: () => {
            invalidateServiceFlowTemplateQueries(queryClient, { organizationID, accountID });
            Toast.show({ type: 'success', text2: 'Template duplicated.' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Something went wrong duplicating that template.' });
        },
    });

    const busy = archiveMutation.isPending || duplicateMutation.isPending;

    const handleArchive = (template, archived) => {
        showAlert({
            title: archived ? `Archive "${template.name}"?` : `Restore "${template.name}"?`,
            subtitle: archived
                ? 'It stays attached to events already using it, but can\'t be newly selected.'
                : 'It can be attached to events again.',
            isDestructive: archived,
            confirmText: archived ? 'Archive' : 'Restore',
            onConfirm: () => archiveMutation.mutate({
                templateID: template.id,
                expectedVersion: template.version,
                archived,
            }),
        });
    };

    let content;
    if (organization && !v2Available) {
        content = (
            <View style={serviceFlowStyles.emptyState}>
                <EmptyListPlaceholder message="Service Flow templates are being rolled out" />
                <Text style={serviceFlowStyles.emptyBody}>
                    Your organization will get reusable service flow templates soon. Existing schedules keep working in the meantime.
                </Text>
            </View>
        );
    } else if (isLoading) {
        content = <EmptyListPlaceholder message="Loading templates…" />;
    } else if (isError) {
        content = (
            <View style={serviceFlowStyles.emptyState}>
                <EmptyListPlaceholder message="Something went wrong loading templates" />
                <SolidButton size="sm" type="events" variant="outlined" text="Try again" onPress={() => refetch()} />
            </View>
        );
    } else if (templates.length === 0) {
        content = (
            <View style={serviceFlowStyles.emptyState}>
                <EmptyListPlaceholder message={showArchived
                    ? 'No archived templates'
                    : search ? 'No templates match that search' : 'No service flow templates yet'} />
                {!showArchived && !search && canManage && (
                    <Text style={serviceFlowStyles.emptyBody}>Create a template to reuse the same flow across events.</Text>
                )}
            </View>
        );
    } else {
        content = templates.map(t => (
            <ServiceFlowTemplateCard
                key={t.id}
                template={t}
                canManage={canManage}
                busy={busy}
                onOpen={() => router.push(`/events/service-flow/${t.id}`)}
                onDuplicate={(tpl) => duplicateMutation.mutate(tpl)}
                onArchive={handleArchive}
            />
        ));
    }

    return (
        <ScrollView
            style={serviceFlowStyles.pageContainer}
            contentContainerStyle={serviceFlowStyles.workspace}
            testID="service-flow-templates"
        >
            <View style={serviceFlowStyles.headerRow}>
                <View style={{ flex: 1, minWidth: 220 }}>
                    <Text style={serviceFlowStyles.eyebrow}>Service Flow</Text>
                    <Text style={serviceFlowStyles.subtitle}>
                        Reusable timelines your events can attach to. Each date still gets its own editable flow.
                    </Text>
                </View>
                {canManage && v2Available && (
                    <SolidButton
                        size="sm"
                        type="events"
                        iconName="add-outline"
                        iconBefore
                        text="New template"
                        onPress={() => router.push('/events/service-flow/create')}
                        testID="service-flow-template-new"
                    />
                )}
            </View>

            {v2Available && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <View style={{ flex: 1, minWidth: 220 }}>
                        <SearchBox
                            placeholder="Search templates"
                            searchText={search}
                            onChangeText={(v) => { setSearch(v); setPage(0); }}
                            testID="service-flow-template-library-search"
                        />
                    </View>
                    <Pressable
                        onPress={() => { setShowArchived(v => !v); setPage(0); }}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 10 }}
                        testID="service-flow-template-show-archived"
                        accessibilityRole="button"
                    >
                        <Unicon name={showArchived ? 'checkbox' : 'square-outline'} size={16} colorVariant={showArchived ? 'events' : 'textSecondary'} />
                        <Text style={serviceFlowStyles.helperText}>Show archived</Text>
                    </Pressable>
                </View>
            )}

            {content}

            {v2Available && pageCount > 1 && (
                <View style={[serviceFlowStyles.headerRow, { justifyContent: 'center' }]}>
                    <SolidButton size="sm" type="primary" variant="outlined" text="Previous" disabled={page === 0 || isFetching} onPress={() => setPage(p => p - 1)} testID="service-flow-template-prev-page" />
                    <Text style={serviceFlowStyles.helperText}>Page {page + 1} of {pageCount}</Text>
                    <SolidButton size="sm" type="primary" variant="outlined" text="Next" disabled={page >= pageCount - 1 || isFetching} onPress={() => setPage(p => p + 1)} testID="service-flow-template-next-page" />
                </View>
            )}
        </ScrollView>
    );
};

export default ServiceFlowLibraryScreen;
